import { Controller } from "@hotwired/stimulus"
import { sendJSON } from "../api.js"
import {
  isFavoriteSubcategory,
  loadFavoriteSubcategories,
  rememberFavoriteSubcategory,
} from "../favorite_subscriptions.js"

// Serduszko ulubionej podkategorii: w sidebarze katalogu i w kontrolce „Kategoria” (≤1180 px) — nigdy w wierszu leada.
// Kliknięcie przełącza subskrypcję backorder (toggle endpoint): wypełnione = zapisane, kontur = nie.
// Optymistyczny UI: stan zmienia się natychmiast; nieudany request cofa zmianę.
//
// Synchronizacja: każda zmiana (optymistyczna, potwierdzona, cofnięta) jest ogłaszana jednym zdarzeniem
// `favorite-heart:changed` (this.dispatch — bąbelkuje do window). Każde serduszko tej samej podkategorii
// nasłuchuje na window i przejmuje stan — sidebar ↔ kontrolka „Kategoria”.
// Stan wyjściowy: z atrybutu active (jeśli jest), inaczej z jednego wspólnego GET /api/backorder-subscriptions/
// na stronę (favorite_subscriptions.js) — serduszka (sidebar + „Kategoria”) nie pobierają listy każde z osobna.
const TOGGLE_URL = "/api/backorder-subscriptions/toggle/"

/** @param {boolean} active */
function heartChar(active) {
  return active ? "♥" : "♡"
}

export default class extends Controller {
  static values = {
    /** Id podkategorii (liczba) — wysyłane w body żądania toggle. */
    subcategoryId: Number,
    /** Czy subskrypcja jest aktywna. Brak atrybutu = stan z wspólnej listy subskrypcji strony. */
    active: Boolean,
  }

  connect() {
    this._touched = false
    this._onChanged = (event) => this.sync(event)
    window.addEventListener("favorite-heart:changed", this._onChanged)
    this.#render()
    if (!this.hasActiveValue) this.#loadInitialState()
  }

  disconnect() {
    window.removeEventListener("favorite-heart:changed", this._onChanged)
    this._onChanged = null
  }

  activeValueChanged() {
    this.#render()
  }

  /** Przejmuje stan od innego serduszka tej samej podkategorii (zdarzenie favorite-heart:changed). */
  sync(event) {
    const detail = event?.detail
    if (!detail || detail.subcategoryId !== this.subcategoryIdValue) return
    this.activeValue = detail.active
  }

  /** Przełącza stan serduszka (dodaj / usuń z ulubionych). */
  async toggle() {
    this._touched = true
    const wasActive = this.activeValue
    // Optymistyczna zmiana — widoczna natychmiast, także w pozostałych serduszkach tej podkategorii.
    this.#announce(!wasActive)

    const res = await sendJSON("POST", TOGGLE_URL, { subcategory: this.subcategoryIdValue })

    if (!res.ok) {
      // Cofnij, jeśli API zwróciło błąd
      this.#announce(wasActive)
      return
    }
    // Potwierdź stan na podstawie odpowiedzi API
    if (res.status === 201) {
      this.#announce(true)
    } else if (res.status === 204) {
      this.#announce(false)
    }
  }

  // --- prywatne ---

  #announce(active) {
    this.activeValue = active
    rememberFavoriteSubcategory(this.subcategoryIdValue, active)
    this.dispatch("changed", { detail: { subcategoryId: this.subcategoryIdValue, active } })
  }

  async #loadInitialState() {
    await loadFavoriteSubcategories()
    // Użytkownik zdążył kliknąć przed odpowiedzią listy — jego stan jest nowszy.
    if (!this.element.isConnected || this._touched) return
    this.activeValue = isFavoriteSubcategory(this.subcategoryIdValue)
  }

  #render() {
    const active = this.activeValue
    this.element.textContent = heartChar(active)
    this.element.setAttribute("aria-pressed", String(active))
    this.element.setAttribute("aria-label", active ? "Usuń z ulubionych" : "Dodaj do ulubionych")
  }
}
